import assert from 'assert';
import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';
import {shallowEqual} from 'react-redux';

import {makeReactContext} from 'lib/makeReactContext';
import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import type {TableOpContextInterface} from 'notebook/tables/spreadsheets/TableOpContext';
import {useNullableTableNoteContext} from 'notebook/tables/store/contexts/TableNoteContext';
import {useTableSpreadsheetStore} from 'notebook/tables/store/hooks/useTableSpreadsheetStore';
import {createTableInteractions} from 'notebook/tables/store/logic/interactions/createTableInteractions';
import {
  createTableMutations,
  UNAVAILABLE_TABLE_MUTATIONS,
} from 'notebook/tables/store/logic/mutations/createTableMutations';
import {createTableModelSelector, TableModel} from 'notebook/tables/store/models/TableModel';
import {
  createTableEditableStore,
  DEFAULT_TABLE_EDITABLE_REDUX_STATE,
  getTablePropsFromStore,
  TableEditableReduxState,
  TableEditableStore,
  TableProps,
} from 'notebook/tables/store/TableStore';

interface TableContextInterface {
  useTableEditableStore: ReturnType<typeof createTableEditableStore>;
  tableModelSelector: (state: TableEditableStore) => TableModel;
  /** Reads the latest props from the store. For event handlers and deferred work only, never render. */
  getTableProps: () => TableProps;
  setState: (state: Partial<TableEditableState>) => void;
}

export const TableModelContext = makeReactContext<TableContextInterface>(() => {
  assert(false, 'TableContext must be used within a TableStateWrapper');
}, 'StructuredTableContext');

export const TableModelProvider = function ({
  children,
  tableProps,
  reduxState = DEFAULT_TABLE_EDITABLE_REDUX_STATE,
  tableOps,
}: {
  children: React.ReactNode;
  tableProps: TableEditableProps;
  /**
   * Redux-derived data populated into the store before the model is generated. Passed in as an
   * opaque object (rather than as TableEditable props) so that it stays internal store state and
   * does not pollute the component's internal or external props. Callers are responsible for
   * creating and memoizing it. Omitted in contexts (e.g. the cross-step formula box) where no
   * redux-derived data is applicable.
   */
  reduxState?: TableEditableReduxState;
  /**
   * The table operations the table's mutations write through. `TableEditable` always passes them.
   * Omitted where the provider only hosts table-scoped models without editing the table structure
   * (e.g. the cross-step formula box), in which case the models carry `UNAVAILABLE_TABLE_MUTATIONS`.
   */
  tableOps?: TableOpContextInterface;
}): JSX.Element {
  const {
    table,
    staleRows,
    errorRows,
    warningRows,
    computedTable,
    overrideShowExcessLabels,
    overrideCellColors,
    ...restStableProps
  } = tableProps;

  // A table missing from the computed-table map arrives here as `undefined`. Default it to the
  // placeholder `[[]]` at the store boundary so both the store selectors and TableEditable read a
  // defined table, while callers upstream of the store keep the real `undefined` (letting their
  // `!= null` checks distinguish "not evaluated" from "empty").
  const definedComputedTable = useMemo(() => computedTable ?? [[]], [computedTable]);

  // `documentName` is only reliably available from the DocumentEditorContext (it is not threaded
  // through as a component prop), so source it from context here to keep the store consistent with
  // what the component reads. See TableEditableComponent's pass-through of `documentName`.
  const {documentName} = DocumentEditorContext.useContext();
  const stableProps = useMemo(() => ({...restStableProps, documentName}), [restStableProps, documentName]);
  const volatileProps = useMemo(
    () => ({table, staleRows, errorRows, warningRows, computedTable: definedComputedTable}),
    [table, staleRows, errorRows, warningRows, definedComputedTable]
  );
  const {tableNamespace, disableFormulas} = restStableProps;

  // The SpreadsheetStore-derived, table-scoped values (`showExcessLabels`, `cellColors`) are
  // subscribed to in this hook and pushed into the store's `spreadsheetState` so that a formula
  // interaction only re-renders the models that read them (axis cells for `showExcessLabels`,
  // individual cells for `cellColors`) rather than invalidating the whole table model.
  const spreadsheetState = useTableSpreadsheetStore(
    tableNamespace,
    table.name,
    disableFormulas ?? false,
    documentName,
    {
      overrideShowExcessLabels,
      overrideCellColors,
    }
  );

  // Extract editor properties from context to store separately.
  const editorContext = EditorPropertiesContext.useContext();

  const tableModelSelector = useMemo(() => createTableModelSelector(), []);

  // The mutations are built before the store so the store can be created with
  // them; they only reach the store through this ref, which is set before anything can call them.
  const storeRef = useRef<ReturnType<typeof createTableEditableStore> | null>(null);
  const getTableModel = useCallback(() => {
    assert(storeRef.current != null, 'The table store must be created before the table model is read');
    return tableModelSelector(storeRef.current.getState());
  }, [tableModelSelector]);
  const getTableProps = useCallback(() => {
    assert(storeRef.current != null, 'The table store must be created before the table props are read');
    return getTablePropsFromStore(storeRef.current.getState());
  }, []);
  const setState = useCallback((state: Partial<TableEditableState>) => {
    assert(storeRef.current != null, 'The table store must be created before its state is set');
    const store = storeRef.current.getState();
    const previousState = store.state;
    const newState = {
      ...previousState,
      ...state,
    };
    if (!isEqual(newState, previousState)) {
      store.setState(newState);
    }
  }, []);

  // Built once per table and put on the TableModel, so every row, cell and axis cell shares this
  // one instance through its own model instead of building its own.
  // Nullable so merge validation still works in isolated contexts (tests/harnesses) that do not provide a
  // note context; in that case only the edited table is scanned for subordinate-cell references.
  const tableNoteContext = useNullableTableNoteContext();
  const {disableMergingCells} = restStableProps;
  const mutations = useMemo(
    () =>
      tableOps === undefined
        ? UNAVAILABLE_TABLE_MUTATIONS
        : createTableMutations(
            {getTableModel, getTableProps, setState, tableNoteContext, tableOps},
            disableMergingCells
          ),
    [getTableModel, getTableProps, setState, tableNoteContext, tableOps, disableMergingCells]
  );

  // Unlike the mutations, the interactions read the latest mutations from the model when called, so
  // they are built only once and keep their identity for the table's lifetime.
  const [interactions] = useState(() => createTableInteractions({getTableModel, getTableProps, setState}));

  // This should explicitly only be created once, and use the initial prop
  const useTableEditableStore = useMemo(() => {
    storeRef.current = createTableEditableStore({
      stableProps,
      volatileProps,
      editorContext,
      reduxState,
      spreadsheetState,
      mutations,
      interactions,
    });
    return storeRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {setTableProps, setEditorContext, setReduxState, setSpreadsheetState, setMutations} =
    useTableEditableStore((store) => ({
      setTableProps: store.setProps,
      setEditorContext: store.setEditorContext,
      setReduxState: store.setReduxState,
      setSpreadsheetState: store.setSpreadsheetState,
      setMutations: store.setMutations,
    }));

  const contextValue: TableContextInterface = useMemo(
    () => ({tableModelSelector, getTableProps, setState, useTableEditableStore}),
    [tableModelSelector, getTableProps, setState, useTableEditableStore]
  );

  // These sync the provider's inputs (props, editor context, redux- and spreadsheet-derived state)
  // into the store, which is the ONLY path by which the memoized `TableEditableComponent` and the
  // rows below it learn about a change: they read from the store, not from these props, and the memo
  // keeps them from re-rendering when this provider re-renders. They must be `useLayoutEffect`, not
  // `useEffect`: a store write here notifies subscribers and schedules their re-render, and only
  // layout effects flush that re-render synchronously — within the same commit that handled the
  // triggering interaction. With a passive `useEffect` the store write (and the subscribers' render)
  // is deferred to a later task, so code that reads the DOM/model right after the interaction (e.g. a
  // paste followed by an assertion) observes the pre-update state. See the double-commit that
  // `React.memo(TableEditableComponent)` otherwise removes.
  // The store keeps its previous volatile props when the new ones are deep-equal, so the last pushed
  // reference is tracked here to avoid re-running that deep comparison on every render.
  const lastPushedVolatilePropsRef = useRef(volatileProps);
  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (
      lastPushedVolatilePropsRef.current !== volatileProps ||
      !shallowEqual(storeState.stableProps, stableProps)
    ) {
      lastPushedVolatilePropsRef.current = volatileProps;
      setTableProps(stableProps, volatileProps);
    }
  }, [useTableEditableStore, setTableProps, volatileProps, stableProps]);

  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (!isEqual(storeState.editorContext, editorContext)) {
      setEditorContext(editorContext);
    }
  }, [useTableEditableStore, setEditorContext, editorContext]);

  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (!isEqual(storeState.reduxState, reduxState)) {
      setReduxState(reduxState);
    }
  }, [useTableEditableStore, setReduxState, reduxState]);

  useLayoutEffect(() => {
    const storeState = useTableEditableStore.getState();
    if (!isEqual(storeState.spreadsheetState, spreadsheetState)) {
      setSpreadsheetState(spreadsheetState);
    }
  }, [useTableEditableStore, setSpreadsheetState, spreadsheetState]);

  // The table ops and note context providers rebuild their values when their own inputs change
  // (e.g. a table's update handler, `allowMergingCells`, or the document id), as can
  // `disableMergingCells`, so the bundle bound to them is replaced in the store.
  useLayoutEffect(() => {
    if (useTableEditableStore.getState().mutations !== mutations) {
      setMutations(mutations);
    }
  }, [useTableEditableStore, setMutations, mutations]);

  return <TableModelContext.Provider value={contextValue}>{children}</TableModelContext.Provider>;
};
